/**
 * HTML escaping for text that ends up inside an outbound email body.
 *
 * One implementation, imported by both users, because they escape the same thing
 * for the same reason and a divergence would be invisible until a stray `&` or
 * `<` became markup in a real message:
 *
 *  - `outlookPills.ts` (`textToEmailHtml`) escapes the model's streamed reply
 *    before it becomes the body of a draft.
 *  - `handlers.ts` (`withOutlookDisclaimer`) escapes the tenant disclaimer, which
 *    Studio authors as plain text and documents as such ("Plain text; HTML is
 *    escaped").
 *
 * Deliberately NOT the inverse of `htmlToClipboardText` in `../clipboard` — that
 * strips markup for the clipboard, which is a different job with different rules.
 */

/**
 * Escape `&`, `<`, `>` and `"` so plain text cannot become markup.
 *
 * Sequential replaces rather than a lookup map: with `noUncheckedIndexedAccess`,
 * indexing a `Record<string, string>` yields `string | undefined`, forcing a `?? c`
 * fallback that the character class can never reach — a branch impossible to cover
 * by any input. `&` must be replaced FIRST, so the ampersands this introduces are
 * not re-escaped by the later passes.
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
